/**
 * Multi-Message Handling Schema
 *
 * Shared across ALL deployment types (WhatsApp, Telegram, Slack, etc.).
 *
 * Two orthogonal axes:
 *   - concurrencyStrategy: what to do when the agent is already running
 *   - debounceMs: optional wait window that groups rapid messages into one trigger
 *
 * They combine freely:
 *   { concurrencyStrategy: "sequential", debounceMs: 3000 }
 *     → collect for 3s, then wait for running agent to finish before triggering
 *   { concurrencyStrategy: "cancel_retrigger", debounceMs: 5000 }
 *     → collect for 5s, cancel running agent if any, then trigger
 *   { concurrencyStrategy: "ignore" }
 *     → fire immediately, drop if agent is already running
 */

import { z } from "zod";

/**
 * What to do when the agent is already running when a (new) trigger fires.
 *
 * - parallel:         fire immediately — agent answers each message independently
 * - ignore:           drop the trigger — user must send a new message to restart
 * - cancel_retrigger: stop the current run, restart with full history
 * - sequential:       let the current run finish, then trigger immediately after
 */
export enum ConcurrencyStrategy {
  PARALLEL = "parallel",
  IGNORE = "ignore",
  CANCEL_RETRIGGER = "cancel_retrigger",
  SEQUENTIAL = "sequential"
}

export const MultiMessageHandlingSchema = z.object({
  concurrencyStrategy: z.nativeEnum(ConcurrencyStrategy).default(ConcurrencyStrategy.SEQUENTIAL),
  /**
   * Optional debounce window in milliseconds.
   * When present, messages are written to DB immediately but the agent trigger
   * is deferred. The timer resets on every incoming message. Once the window
   * elapses without a new message, the agent is triggered once for all
   * accumulated messages. Min: 1s. Max: 60s.
   */
  debounceMs: z.number().int().min(1000).max(60000).optional()
});

export type MultiMessageHandling = z.infer<typeof MultiMessageHandlingSchema>;

/**
 * Default: 3-second debounce window + sequential queue.
 * Collects rapid message bursts, waits for the current run to finish before
 * triggering — prevents duplicate greetings and interleaved responses.
 */
export const DEFAULT_MULTI_MESSAGE_HANDLING: MultiMessageHandling = {
  concurrencyStrategy: ConcurrencyStrategy.SEQUENTIAL,
  debounceMs: 3000
};
